import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Box,
  Chip,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Refresh,
  Storage,
  Router,
  CheckCircle,
  PriorityHigh,
  Visibility,
  Download,
  Computer,
  PowerSettingsNew,
  RestartAlt,
  InsertDriveFile,
  ArrowDropDown,
  CorporateFare,
} from "@mui/icons-material";
import PropTypes from "prop-types";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import DataTable from "shared/components/Tables/DataTable";

import TableHeader from "./components/TableHeader";
import ReadersModal from "./components/ReadersModal";
import ResetAllReadersDialog from "./components/ResetAllReadersDialog";
import ResetEDSDialog from "./components/ResetEDSDialog";
import ResetCommerceReadersDialog from "./components/ResetCommerceReadersDialog";
import ProcessingDialog from "./components/ProcessingDialog";
import SearchBar from "./components/SearchBar";
import { filterReaderGroups } from "../../model/readersModel";
import StatisticsPanel from "./components/StatisticsPanel";
import { useReaders } from "../../hooks/useReaders";

function TableReaders({ refreshFlag, onRefreshComplete }) {
  const {
    readerGroups,
    lastFetchedAt,
    loading,
    resetAllLoading,
    resetEDSLoading,
    resetCommerceLoading,
    resetIndividualLoading,
    sendingPulse,
    gettingPower,
    settingPower,
    gettingStatus,
    togglingPower,
    exporting,
    error,
    selectedReader,
    selectedReaderElement,
    notification,
    stats,
    edsResetProgress,

    showNotification,
    closeNotification,

    fetchReaders,
    refreshReaderData,
    exportToExcel,
    exportAdvancedReport,
    resetAllReaders,
    resetEDSReaders,
    resetReaderByCommerce,
    resetReaderById,

    selectReaderGroup,
    selectReaderElement,
    clearSelection,

    handleSendPulse,
    getAntennaPower,
    setAntennaPower,
    setAntennaPowerStatus,
    getAntennaStatus,
    getAntennasStatusByReader,
    getUniqueCommerces,

    findReaderByAntennaId,
    checkAntennaCooldown,
  } = useReaders();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const filteredReaderGroups = useMemo(
    () => filterReaderGroups(readerGroups, searchQuery, statusFilter),
    [readerGroups, searchQuery, statusFilter]
  );
  const operationsBusy =
    resetAllLoading || resetEDSLoading || resetCommerceLoading || resetIndividualLoading;
  const [processingDialogOpen, setProcessingDialogOpen] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [readersModalOpen, setReadersModalOpen] = useState(false);
  const [resetAllDialogOpen, setResetAllDialogOpen] = useState(false);
  const [resetEDSDialogOpen, setResetEDSDialogOpen] = useState(false);
  const [resetCommerceDialogOpen, setResetCommerceDialogOpen] = useState(false);
  const [selectedCommerce, setSelectedCommerce] = useState(null);
  const [statusChanging, setStatusChanging] = useState(false);
  const [selectedReaderForOperation, setSelectedReaderForOperation] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const fetchReadersRef = React.useRef(fetchReaders);
  const previousRefreshFlag = React.useRef(refreshFlag);
  useEffect(() => {
    fetchReadersRef.current = fetchReaders;
  }, [fetchReaders]);

  const handleGetAntennaPower = useCallback(
    async (antennaId) => {
      try {
        return await getAntennaPower(antennaId);
      } catch (error) {
        console.error("❌ Error al obtener potencia de antena:", error);
        return {
          success: false,
          error: error.message || "Error al obtener potencia",
        };
      }
    },
    [getAntennaPower]
  );

  const handleSetAntennaPower = useCallback(
    async (antennaId, powerValue) => {
      try {
        const result = await setAntennaPower(antennaId, powerValue);
        if (result?.success) {
          showNotification(
            `Potencia configurada para antena con ID ${antennaId}: ${(powerValue / 100).toFixed(
              1
            )} dBm`,
            "success"
          );
        }
        return result;
      } catch (error) {
        console.error("❌ Error al configurar potencia:", error);
        throw error;
      }
    },
    [setAntennaPower, showNotification]
  );

  const handleSetAntennaPowerStatus = useCallback(
    async (antennaId, state) => {
      try {
        const result = await setAntennaPowerStatus(antennaId, state);
        return result;
      } catch (error) {
        console.error("❌ Error al cambiar estado de antena:", error);
        throw error;
      }
    },
    [setAntennaPowerStatus]
  );

  const handleGetAntennaStatus = useCallback(
    async (antennaId) => {
      try {
        const result = await getAntennaStatus(antennaId);
        if (result?.success) {
          showNotification(`Estado de antena ${antennaId}: ${result.status}`, "success");
        }
        return result;
      } catch (error) {
        console.error("❌ Error al obtener estado de antena:", error);
        throw error;
      }
    },
    [getAntennaStatus, showNotification]
  );

  const handleSendPulseToAntenna = useCallback(
    async (antennaId) => {
      try {
        const result = await handleSendPulse(antennaId);
        if (result?.success) {
          showNotification(`Pulso enviado a antena con ID: ${antennaId} correctamente`, "success");
        }
        return result;
      } catch (error) {
        console.error("❌ Error al enviar pulso:", error);
        throw error;
      }
    },
    [handleSendPulse, showNotification]
  );

  useEffect(() => {
    fetchReadersRef.current();
  }, []);

  useEffect(() => {
    if (previousRefreshFlag.current !== refreshFlag) {
      previousRefreshFlag.current = refreshFlag;
      fetchReadersRef.current().finally(() => onRefreshComplete?.());
    }
  }, [refreshFlag, onRefreshComplete]);

  const getReadersByCommerce = useCallback(
    (commerceName) => {
      try {
        if (commerceName && readerGroups.length > 0) {
          const allReaders = readerGroups.flatMap((group) => group.readers);

          const filteredReaders = allReaders.filter(
            (reader) => reader.commerce && reader.commerce === commerceName
          );

          if (filteredReaders.length === 0) {
            const commerceGroup = readerGroups.find(
              (group) => group.name === commerceName || group.hostname === commerceName
            );

            if (commerceGroup && commerceGroup.readers && Array.isArray(commerceGroup.readers)) {
              return commerceGroup.readers.map((reader) => ({
                id: reader.id,
                hostname: reader.hostname,
                ip: reader.ip,
                commerce: reader.commerce || commerceName,
                status: reader.status,
                antennas: reader.antennas || [],
                more: reader.more || null,
              }));
            }
          }

          return filteredReaders.map((reader) => ({
            id: reader.id,
            hostname: reader.hostname,
            ip: reader.ip,
            commerce: reader.commerce,
            status: reader.status,
            antennas: reader.antennas || [],
            more: reader.more || null,
          }));
        }
      } catch (error) {
        console.error("Error obteniendo readers por comercio:", error);
      }

      return [];
    },
    [readerGroups]
  );

  const handleChangeReaderStatus = useCallback(
    async (reader) => {
      setStatusChanging(true);
      setSelectedReaderForOperation(reader);

      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        showNotification(`Estado del reader ${reader.hostname} actualizado`, "success");
        fetchReaders();
      } catch (error) {
        showNotification(`Error al cambiar estado: ${error.message}`, "error");
      } finally {
        setStatusChanging(false);
        setSelectedReaderForOperation(null);
      }
    },
    [showNotification, fetchReaders]
  );

  const handleViewReaders = useCallback(
    (readerGroup) => {
      selectReaderGroup(readerGroup);
      setReadersModalOpen(true);
    },
    [selectReaderGroup]
  );

  const handleOpenResetCommerceDialog = useCallback((commerce) => {
    if (!commerce) return;
    setSelectedCommerce(commerce);
    setResetCommerceDialogOpen(true);
  }, []);

  const handleResetAllReaders = useCallback(async () => {
    try {
      const result = await resetAllReaders();
      if (result.success) {
        showNotification("Reinicio masivo de readers iniciado correctamente", "success");
      }
      return result;
    } catch (error) {
      console.error("Error en reinicio masivo:", error);
      throw error;
    }
  }, [resetAllReaders, showNotification]);

  const handleResetEDSReaders = useCallback(async () => {
    try {
      const result = await resetEDSReaders(10, 2000);
      if (result.success) {
        showNotification(
          `Reinicio de comercios EDS iniciado: ${
            result.totalEDSReadersActive || result.totalEDSReaders
          } readers activos`,
          "success"
        );
      }
      return result;
    } catch (error) {
      console.error("Error en reinicio EDS:", error);
      throw error;
    }
  }, [resetEDSReaders, showNotification]);

  const handleResetCommerceReaders = useCallback(
    async (commerceName) => {
      try {
        const result = await resetReaderByCommerce(commerceName);
        if (result.success) {
          showNotification(
            `Readers del comercio ${commerceName} reiniciados correctamente`,
            "success"
          );
        }
        return result;
      } catch (error) {
        console.error("Error al reiniciar readers por comercio:", error);
        throw error;
      }
    },
    [resetReaderByCommerce, showNotification]
  );

  const handleResetIndividualReader = useCallback(
    async (reader) => {
      try {
        const result = await resetReaderById(reader);
        if (result.success) {
          showNotification(`Reader ${reader.hostname} reiniciado correctamente`, "success");
        }
        return result;
      } catch (error) {
        console.error("Error al reiniciar reader individual:", error);
        throw error;
      }
    },
    [resetReaderById, showNotification]
  );

  const handleExportReport = useCallback(async () => {
    setExportMenuAnchor(null);
    setProcessingMessage("📊 Generando informe básico de readers...");
    setProcessingDialogOpen(true);

    try {
      const result = await exportToExcel(false, filteredReaderGroups);

      if (result && result.success) {
        setProcessingMessage(`✅ Informe exportado: ${result.recordsCount} registros`);
        setTimeout(() => setProcessingDialogOpen(false), 2000);
      } else {
        throw new Error(result?.error || "Error al exportar");
      }
    } catch (error) {
      console.error("Error al exportar el informe:", error);
      setProcessingMessage(`❌ Error: ${error.message}`);
      setTimeout(() => setProcessingDialogOpen(false), 3000);
    }
  }, [exportToExcel, filteredReaderGroups]);

  const handleExportAdvancedReport = useCallback(async () => {
    setExportMenuAnchor(null);
    setProcessingMessage("📈 Generando informe avanzado...");
    setProcessingDialogOpen(true);

    try {
      const result = await exportAdvancedReport();

      if (result && result.success) {
        setProcessingMessage(`✅ Reporte avanzado exportado: ${result.totalReaders} readers`);
        setTimeout(() => setProcessingDialogOpen(false), 2000);
      } else {
        throw new Error(result?.error || "Error al exportar reporte avanzado");
      }
    } catch (error) {
      console.error("Error al exportar reporte avanzado:", error);
      setProcessingMessage(`❌ Error: ${error.message}`);
      setTimeout(() => setProcessingDialogOpen(false), 3000);
    }
  }, [exportAdvancedReport]);

  const handleExportCompleteData = useCallback(async () => {
    setExportMenuAnchor(null);
    setProcessingMessage("🔍 Descargando datos completos del servidor...");
    setProcessingDialogOpen(true);

    try {
      const result = await exportToExcel(true);

      if (result && result.success) {
        setProcessingMessage(`✅ Datos completos exportados: ${result.recordsCount} registros`);
        setTimeout(() => setProcessingDialogOpen(false), 2000);
      } else {
        throw new Error(result?.error || "Error al exportar datos completos");
      }
    } catch (error) {
      console.error("Error al exportar datos completos:", error);
      setProcessingMessage(`❌ Error: ${error.message}`);
      setTimeout(() => setProcessingDialogOpen(false), 3000);
    }
  }, [exportToExcel]);

  const handleOpenExportMenu = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchor(null);
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Nunca";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formateando fecha:", error);
      return "Fecha inválida";
    }
  }, []);

  const truncateText = useCallback((text, maxLength = 20) => {
    if (!text) return "N/A";
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  }, []);

  const uniqueCommerces = useMemo(() => {
    const commerceCounts = {};
    readerGroups.forEach((group) => {
      group.readers.forEach((reader) => {
        const commerce = reader.commerce || "Sin comercio";
        commerceCounts[commerce] = (commerceCounts[commerce] || 0) + 1;
      });
    });

    return Object.entries(commerceCounts).map(([commerce, count]) => ({
      name: commerce,
      count,
      activeReaders: readerGroups
        .flatMap((g) => g.readers)
        .filter((r) => r.commerce === commerce && r.status).length,
    }));
  }, [readerGroups]);

  const columns = useMemo(
    () => [
      { Header: "Nombre del Grupo", accessor: "nombre", width: "25%" },
      { Header: "Comercio", accessor: "comercio", width: "20%" },
      { Header: "Tipo Reader", accessor: "reader_type", width: "15%" },
      { Header: "Readers", accessor: "readers", width: "15%" },
      { Header: "Acciones", accessor: "acciones", align: "center", width: "25%" },
    ],
    []
  );

  const getActiveReadersByCommerceInGroup = useCallback((group, commerceName) => {
    if (!group || !commerceName) return 0;
    return group.readers.filter((reader) => reader.commerce === commerceName && reader.status)
      .length;
  }, []);

  const rows = useMemo(
    () =>
      filteredReaderGroups.map((group) => {
        const hasActiveReaders = group.activeReaders > 0;

        const commerceCounts = {};
        group.readers.forEach((reader) => {
          const commerce = reader.commerce || "Sin comercio";
          commerceCounts[commerce] = (commerceCounts[commerce] || 0) + 1;
        });

        const mostCommonCommerce = Object.entries(commerceCounts)
          .sort((a, b) => b[1] - a[1])
          .map(([commerce]) => commerce)[0];

        const primaryIp = group.readers.length > 0 ? group.readers[0].ip : "Sin IP";
        const activeReadersInCommerce = getActiveReadersByCommerceInGroup(
          group,
          mostCommonCommerce
        );

        return {
          nombre: (
            <MDBox display="flex" alignItems="center">
              {hasActiveReaders && (
                <Tooltip title="Tiene readers activos" arrow>
                  <PriorityHigh sx={{ color: "#2e7d32", mr: 1, fontSize: "1rem" }} />
                </Tooltip>
              )}
              <MDBox>
                <MDTypography variant="button" fontWeight="medium" sx={{ color: "#424242" }}>
                  <Router
                    sx={{ mr: 1, verticalAlign: "middle", fontSize: "1rem", color: "#1976d2" }}
                  />
                  {group.name || "Sin nombre"}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: "#757575" }} display="block">
                  {group.totalReaders} readers configurados
                </MDTypography>
              </MDBox>
            </MDBox>
          ),
          comercio: (
            <Tooltip title={mostCommonCommerce || "Sin comercio"} arrow>
              <Chip
                label={truncateText(mostCommonCommerce, 20)}
                size="small"
                sx={{
                  color: "#7b1fa2",
                  borderColor: "#7b1fa2",
                  backgroundColor: "rgba(123, 31, 162, 0.08)",
                }}
                variant="outlined"
                icon={<Storage sx={{ color: "#7b1fa2" }} fontSize="small" />}
              />
            </Tooltip>
          ),
          reader_type: (
            <Box>
              {Array.from(new Set(group.readers.map((r) => r.reader_type || "Estándar"))).map(
                (type) => (
                  <Chip
                    key={type}
                    label={type}
                    size="small"
                    sx={{
                      m: 0.5,
                      color: type === "hura" ? "#7b1fa2" : "#0288d1",
                      borderColor: type === "hura" ? "#7b1fa2" : "#0288d1",
                      backgroundColor:
                        type === "hura" ? "rgba(123, 31, 162, 0.08)" : "rgba(2, 136, 209, 0.08)",
                      textTransform: "capitalize",
                    }}
                    variant="outlined"
                  />
                )
              )}
            </Box>
          ),
          readers: (
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label={`${group.activeReaders}/${group.totalReaders}`}
                size="small"
                sx={{
                  backgroundColor: hasActiveReaders ? "#2e7d32" : "transparent",
                  color: hasActiveReaders ? "#fff" : "#757575",
                  borderColor: hasActiveReaders ? "#2e7d32" : "#e0e0e0",
                  fontWeight: hasActiveReaders ? "bold" : "normal",
                }}
                variant={hasActiveReaders ? "filled" : "outlined"}
              />
              <MDTypography variant="caption" sx={{ color: "#757575" }}>
                activos
              </MDTypography>
            </Box>
          ),
          acciones: (
            <Box display="flex" gap={1} justifyContent="center" alignItems="center">
              <Tooltip title="Ver readers de este grupo" arrow>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    color: "#0288d1",
                    borderColor: "#0288d1",
                    "&:hover": {
                      borderColor: "#01579b",
                      backgroundColor: "rgba(2, 136, 209, 0.04)",
                    },
                    minWidth: 120,
                    textTransform: "none",
                    fontSize: "0.75rem",
                    fontWeight: "medium",
                  }}
                  startIcon={<Visibility fontSize="small" />}
                  onClick={() => handleViewReaders(group)}
                >
                  Ver Readers
                </Button>
              </Tooltip>

              {activeReadersInCommerce > 0 && mostCommonCommerce && (
                <Tooltip
                  title={`Reiniciar todos los readers del comercio ${mostCommonCommerce}`}
                  arrow
                >
                  <span style={{ display: "inline-flex" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        color: "#ff9800",
                        borderColor: "#ff9800",
                        "&:hover": {
                          borderColor: "#f57c00",
                          backgroundColor: "rgba(255, 152, 0, 0.04)",
                        },
                        minWidth: 140,
                        textTransform: "none",
                        fontSize: "0.75rem",
                        fontWeight: "medium",
                      }}
                      disabled={operationsBusy}
                      startIcon={<RestartAlt fontSize="small" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenResetCommerceDialog(mostCommonCommerce);
                      }}
                    >
                      Reiniciar Comercio
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          ),
        };
      }),
    [
      filteredReaderGroups,
      truncateText,
      handleViewReaders,
      handleOpenResetCommerceDialog,
      getActiveReadersByCommerceInGroup,
      operationsBusy,
    ]
  );

  return (
    <>
      <TableHeader
        title="Gestión de Readers"
        subtitle="Sistema de automatización de acceso"
        stats={stats}
        loading={loading}
        onRefresh={fetchReaders}
        lastFetchedAt={lastFetchedAt}
        disabled={operationsBusy}
      />
      <StatisticsPanel stats={stats} loading={loading} />

      <MDBox
        px={2}
        pt={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Box sx={{ flex: "1 1 520px", minWidth: 0, mr: { md: 2 } }}>
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
          />
        </Box>

        <MDBox display="flex" gap={1} flexWrap="wrap">
          <Tooltip
            title={`Reiniciar solo comercios EDS (${
              stats?.edsReadersCount || 0
            } readers ACTIVOS de ${stats?.edsReadersTotal || 0} totales)`}
            arrow
          >
            <span style={{ display: "inline-flex" }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CorporateFare />}
                onClick={() => setResetEDSDialogOpen(true)}
                disabled={
                  loading ||
                  operationsBusy ||
                  readerGroups.length === 0 ||
                  (stats?.edsReadersCount || 0) === 0
                }
                sx={{
                  textTransform: "none",
                  fontWeight: "medium",
                  color: "#ffffff",
                  borderRadius: "8px",
                  px: 2,
                  backgroundColor: "#7b1fa2",
                  "&:hover": { backgroundColor: "#481766ff", color: "#ffffff" },
                }}
              >
                {resetEDSLoading
                  ? "REINICIANDO EDS..."
                  : `Reiniciar EDS (${stats?.edsReadersCount || 0} activos)`}
              </Button>
            </span>
          </Tooltip>

          <Tooltip
            title={`Reiniciar todos los readers activos (${stats?.activeReaders || 0} activos)`}
            arrow
          >
            <span style={{ display: "inline-flex" }}>
              <Button
                variant="contained"
                color="warning"
                startIcon={<PowerSettingsNew />}
                onClick={() => setResetAllDialogOpen(true)}
                disabled={
                  loading ||
                  operationsBusy ||
                  readerGroups.length === 0 ||
                  stats?.activeReaders === 0
                }
                sx={{
                  textTransform: "none",
                  fontWeight: "medium",
                  color: "#ffffffff",
                  borderRadius: "8px",
                  px: 2,
                  backgroundColor: "#ff9800",
                  "&:hover": { backgroundColor: "#f57c00" },
                  "&:disabled": {
                    backgroundColor: "rgba(255, 152, 0, 0.5)",
                    color: "rgba(255, 255, 255, 0.5)",
                  },
                }}
              >
                Reiniciar Todos
              </Button>
            </span>
          </Tooltip>

          <Box>
            <Tooltip title="Exportar informe de readers" arrow>
              <span style={{ display: "inline-flex" }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Download />}
                  endIcon={<ArrowDropDown />}
                  onClick={handleOpenExportMenu}
                  disabled={loading || exporting || filteredReaderGroups.length === 0}
                  sx={{
                    textTransform: "none",
                    fontWeight: "medium",
                    borderRadius: "8px",
                    px: 2,
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#1b5e20" },
                    "&:disabled": {
                      backgroundColor: "rgba(46, 125, 50, 0.5)",
                      color: "rgba(255, 255, 255, 0.5)",
                    },
                  }}
                >
                  {exporting ? "EXPORTANDO..." : "Exportar Informe"}
                </Button>
              </span>
            </Tooltip>

            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={handleCloseExportMenu}
              PaperProps={{ sx: { mt: 1, minWidth: 250, boxShadow: 3 } }}
            >
              <MenuItem onClick={handleExportReport} disabled={exporting}>
                <ListItemIcon>
                  <InsertDriveFile fontSize="small" sx={{ color: "#2e7d32" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Informe básico"
                  secondary="Todos los readers de los grupos filtrados"
                  primaryTypographyProps={{ fontWeight: "medium" }}
                />
              </MenuItem>
            </Menu>
          </Box>
        </MDBox>
      </MDBox>

      <MDBox
        px={2}
        pt={1}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
      >
        <MDTypography variant="caption" sx={{ color: "#757575" }}>
          <PriorityHigh
            sx={{ fontSize: "0.75rem", mr: 0.5, verticalAlign: "middle", color: "#2e7d32" }}
          />
          Los grupos con readers activos aparecen primero
        </MDTypography>
        <MDTypography variant="caption" sx={{ color: "#757575" }}>
          Mostrando {filteredReaderGroups.length} de {readerGroups.length} grupos
          {searchQuery && ` (filtrados por "${searchQuery}")`}
        </MDTypography>
      </MDBox>

      <MDBox
        pt={2}
        sx={{
          "& .MuiTableCell-root": { fontSize: "0.85rem", py: 1.5 },
          "& .MuiTableContainer-root": { maxHeight: 800 },
          "& thead th": {
            bgcolor: "#f1f5f9",
            color: "#334155",
            position: "sticky",
            top: 0,
            zIndex: 1,
          },
          "& tbody tr:hover": { bgcolor: "#f8fafc" },
        }}
      >
        {loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress sx={{ color: "#0288d1" }} size={60} />
            <MDTypography variant="body2" sx={{ color: "#424242" }} ml={2}>
              Cargando readers...
            </MDTypography>
          </MDBox>
        ) : error ? (
          <MDBox p={2}>
            <Alert
              severity="error"
              action={
                <IconButton sx={{ color: "#d32f2f" }} size="small" onClick={fetchReaders}>
                  <Refresh />
                </IconButton>
              }
              sx={{ "& .MuiAlert-icon": { color: "#d32f2f" } }}
            >
              {error}
            </Alert>
          </MDBox>
        ) : filteredReaderGroups.length === 0 ? (
          <MDBox
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
            p={3}
          >
            <Computer sx={{ fontSize: 60, color: "#616161", mb: 2 }} />
            <MDTypography variant="h6" sx={{ color: "#424242" }} gutterBottom>
              {searchQuery ? "No se encontraron resultados" : "No hay readers configurados"}
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: "#757575" }}>
              {searchQuery
                ? "Intenta con otros términos de búsqueda"
                : "Configura readers en el sistema de automatización"}
            </MDTypography>
          </MDBox>
        ) : (
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            autoResetPage={false}
            paginationResetKey={`${searchQuery}|${statusFilter}`}
            entriesPerPage={{ defaultValue: 25, entries: [10, 25, 50, 100, 200] }}
            showTotalEntries={true}
            pagination={{ variant: "gradient", color: "info" }}
            canSearch={false}
            noEndBorder
          />
        )}
      </MDBox>

      <ReadersModal
        open={readersModalOpen}
        onClose={() => {
          setReadersModalOpen(false);
          clearSelection();
        }}
        readerGroup={selectedReader}
        loading={loading}
        resetIndividualLoading={resetIndividualLoading}
        sendingPulse={sendingPulse}
        gettingPower={gettingPower}
        settingPower={settingPower}
        gettingStatus={gettingStatus}
        togglingPower={togglingPower}
        onResetReader={handleResetIndividualReader}
        onSendPulse={handleSendPulse}
        getAntennaPower={handleGetAntennaPower}
        setAntennaPower={handleSetAntennaPower}
        getAntennaStatus={handleGetAntennaStatus}
        onGetAntennasStatus={getAntennasStatusByReader}
        onSetPowerStatus={handleSetAntennaPowerStatus}
        formatDate={formatDate}
      />

      <ResetAllReadersDialog
        open={resetAllDialogOpen}
        onClose={() => setResetAllDialogOpen(false)}
        onConfirm={handleResetAllReaders}
        readerGroups={readerGroups}
        stats={stats}
        resetAllLoading={resetAllLoading}
      />

      <ResetEDSDialog
        open={resetEDSDialogOpen}
        onClose={() => setResetEDSDialogOpen(false)}
        onConfirm={handleResetEDSReaders}
        readerGroups={readerGroups}
        stats={stats}
        resetEDSLoading={resetEDSLoading}
        edsResetProgress={edsResetProgress}
      />

      <ResetCommerceReadersDialog
        open={resetCommerceDialogOpen}
        onClose={() => {
          setResetCommerceDialogOpen(false);
          setSelectedCommerce(null);
        }}
        onConfirm={handleResetCommerceReaders}
        commerceName={selectedCommerce}
        commerceData={readerGroups.find(
          (group) => group.name === selectedCommerce || group.hostname === selectedCommerce
        )}
        getCommerceReaders={getReadersByCommerce}
        resetCommerceLoading={resetCommerceLoading}
      />

      <ProcessingDialog
        open={processingDialogOpen}
        message={processingMessage}
        onClose={() => setProcessingDialogOpen(false)}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          <MDTypography variant="body2" fontWeight="medium">
            {notification.message}
          </MDTypography>
        </Alert>
      </Snackbar>
    </>
  );
}

TableReaders.propTypes = {
  refreshFlag: PropTypes.bool,
  onRefreshComplete: PropTypes.func,
};

TableReaders.defaultProps = {
  refreshFlag: false,
  onRefreshComplete: () => {},
};

export default TableReaders;
